import { useContext, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PencilLine, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { MyContext } from '../../context';
import DataTable from '../../components/tables/DataTable';
import Pagination from '../../components/Pagination';
import StatusBadge from '../../components/StatusBadge';
import RowCheckbox from '../../components/RowCheckbox';
import Tooltip from '../../components/Tooltip';
import DialogModal from '../../components/dialog';
import FilterPills from '../../components/FilterPills';
import DateRangePicker from '../../components/DateRangePicker';
import {
	listOrders,
	listPendingApprovals,
	listPendingReviews,
	bulkOrderAction,
	approveOrder,
	rejectOrder,
	releaseOrder,
} from '../../utils/queries';
import { getListStatus } from '../../utils/orderStatus';
import { formatDate, formatShipDate, isWeekendDate, shipCalendarToday } from '../../utils/format';
import { useCan } from '../../hooks/useCan';
import { ADMIN_ROLES } from '../../components/navConfig';

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_STORAGE_KEY = 'orders.pageSize';
const ERP_JOB_TOOLTIP_ID = 'orders-erp-job-tooltip';

const readStoredPageSize = () => {
	try {
		const stored = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
		return PAGE_SIZE_OPTIONS.includes(stored) ? stored : DEFAULT_PAGE_SIZE;
	} catch {
		return DEFAULT_PAGE_SIZE;
	}
};

// Client-side hints only, so the dialogs can say how many selected orders
// an action applies to -- the API re-checks each order and reports anything
// it skipped.
const isApprovable = (row) => row.state === 'PENDING_APPROVAL';
const isReleasable = (row) => row.state === 'PENDING_REVIEW';
const isOpen = (row) => row.state !== 'CANCELLED' && !row.shipped;

// view=approval comes from the URL so the old /approvals links (approval
// request emails, the Command Center tile) can redirect straight into it.
const VIEW_ALL = 'all';
const VIEW_APPROVAL = 'approval';
// Manual-review gate (stores.require_order_review) -- any store admin.
const VIEW_REVIEW = 'review';

// The approvals/reviews endpoints return a slimmer row than /admin/orders;
// fill in what the shared columns and bulk actions read. Every row each one
// returns is in that view's state by definition.
const toOrderRow = (state) => (row) => ({
	...row,
	state,
	shipped: false,
	anticipated_ship_date: null,
	points_required: null,
});

// Wraps a pending-list response in the { orders, pagination } shape
// listOrders returns, so the effect below can treat every view the same.
const asOrdersResult = (result, key, state) =>
	result?.success
		? {
				...result,
				data: {
					orders: (result.data[key] || []).map(toOrderRow(state)),
					pagination: result.data.pagination,
				},
			}
		: result;

// The API compares dateTo against order_date (a datetime), so a bare
// 'YYYY-MM-DD' would drop orders placed on the last day of the range.
const endOfDay = (date) => (date ? `${date} 23:59:59` : undefined);

const ACTION_LABELS = {
	approve: { verb: 'approved', title: 'Approve orders' },
	release: { verb: 'released', title: 'Release orders to ERP' },
	cancel: { verb: 'cancelled', title: 'Cancel orders' },
	set_ship_date: { verb: 'updated', title: 'Set expected ship date' },
};

export default function OrdersList() {
	const { activeStoreId, activeStoreIsOrderApprover, stores } = useContext(MyContext);
	const activeStore = stores?.find((s) => s.storeId === activeStoreId);
	const isAdmin = useCan(ADMIN_ROLES);
	const canApprove = isAdmin || activeStoreIsOrderApprover;
	const canSelect = isAdmin || canApprove;

	const [searchParams, setSearchParams] = useSearchParams();
	const requestedView = searchParams.get('view');
	const view =
		canApprove && requestedView === VIEW_APPROVAL
			? VIEW_APPROVAL
			: isAdmin && requestedView === VIEW_REVIEW
				? VIEW_REVIEW
				: VIEW_ALL;
	// Approval-request emails link to a specific order; highlight its row
	// rather than filtering down to it, so it stays in context.
	const highlightOrderId = searchParams.get('orderId') ? Number(searchParams.get('orderId')) : null;
	const [range, setRange] = useState({});
	const [pickerOpen, setPickerOpen] = useState(false);

	const [orders, setOrders] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(readStoredPageSize);
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [refreshKey, setRefreshKey] = useState(0);

	const [selectedIds, setSelectedIds] = useState(() => new Set());
	// Checkboxes stay hidden until the select column is hovered (or one is
	// focused / something is already selected), so they don't clutter the list.
	const [selectColumnHovered, setSelectColumnHovered] = useState(false);
	const [bulkAction, setBulkAction] = useState(null);
	const [bulkReason, setBulkReason] = useState('Cancelled by admin');
	const [bulkShipDate, setBulkShipDate] = useState('');
	const [bulkWorking, setBulkWorking] = useState(false);
	// Second step before anything is sent: a "are you sure?" popup. Approve has
	// nothing to fill in, so it opens straight on this step.
	const [bulkConfirming, setBulkConfirming] = useState(false);
	const [bulkError, setBulkError] = useState('');
	const [bulkResults, setBulkResults] = useState(null);

	useEffect(() => {
		setLoading(true);
		const params = {
			limit: pageSize,
			offset: page * pageSize,
			search: search || undefined,
			storeId: activeStoreId || undefined,
			dateFrom: range.from || undefined,
			dateTo: endOfDay(range.to),
		};
		const request =
			view === VIEW_APPROVAL
				? listPendingApprovals(params).then((result) => asOrdersResult(result, 'approvals', 'PENDING_APPROVAL'))
				: view === VIEW_REVIEW
					? listPendingReviews(params).then((result) => asOrdersResult(result, 'reviews', 'PENDING_REVIEW'))
					: listOrders(params);
		request.then((result) => {
			if (result?.success) {
				setOrders(result.data.orders);
				setTotal(result.data.pagination.total);
			}
			setLoading(false);
		});
	}, [view, page, pageSize, search, activeStoreId, range.from, range.to, refreshKey]);

	// Selection is per page: anything that changes which rows are showing clears it.
	useEffect(() => {
		setSelectedIds(new Set());
	}, [view, page, pageSize, search, activeStoreId, range.from, range.to]);

	const changeView = (next) => {
		setPage(0);
		setSearchParams(next === VIEW_ALL ? {} : { view: next });
	};

	const changeRange = (next) => {
		setRange(next || {});
		setPage(0);
		setPickerOpen(false);
	};

	// ── Single-order approve / decline (needs-approval view) ──
	const [approveTarget, setApproveTarget] = useState(null);
	const [declineTarget, setDeclineTarget] = useState(null);
	const [declineReason, setDeclineReason] = useState('');
	const [decisionWorking, setDecisionWorking] = useState(false);

	const handleApproveConfirm = async () => {
		if (!approveTarget) return;
		setDecisionWorking(true);
		const result = await approveOrder(approveTarget.order_id);
		setDecisionWorking(false);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to approve order.');
			return;
		}
		toast.success(`Order ${approveTarget.order_number} approved.`);
		setApproveTarget(null);
		setRefreshKey((k) => k + 1);
	};

	const handleDeclineConfirm = async () => {
		if (!declineTarget || !declineReason.trim()) return;
		setDecisionWorking(true);
		const result = await rejectOrder(declineTarget.order_id, declineReason.trim());
		setDecisionWorking(false);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to decline order.');
			return;
		}
		toast.success(`Order ${declineTarget.order_number} declined.`);
		setDeclineTarget(null);
		setDeclineReason('');
		setRefreshKey((k) => k + 1);
	};

	// ── Single-order release (needs-review view) ──
	const [releaseTarget, setReleaseTarget] = useState(null);

	const handleReleaseConfirm = async () => {
		if (!releaseTarget) return;
		setDecisionWorking(true);
		const result = await releaseOrder(releaseTarget.order_id);
		setDecisionWorking(false);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to release order.');
			return;
		}
		toast.success(`Order ${releaseTarget.order_number} released to the ERP queue.`);
		setReleaseTarget(null);
		setRefreshKey((k) => k + 1);
	};

	const changePageSize = (size) => {
		setPageSize(size);
		setPage(0);
		try {
			localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(size));
		} catch {
			/* per-viewer convenience only */
		}
	};

	const pageIds = orders.map((o) => o.order_id);
	const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
	const someSelected = !allSelected && pageIds.some((id) => selectedIds.has(id));
	const selectedRows = orders.filter((o) => selectedIds.has(o.order_id));

	const toggleSelectAll = () => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (allSelected) pageIds.forEach((id) => next.delete(id));
			else pageIds.forEach((id) => next.add(id));
			return next;
		});
	};

	const toggleRow = (orderId) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(orderId)) next.delete(orderId);
			else next.add(orderId);
			return next;
		});
	};

	const openBulk = (action) => {
		setBulkError('');
		setBulkReason('Cancelled by admin');
		setBulkShipDate('');
		// Approve and release have nothing to fill in, so they open on the confirm step.
		setBulkConfirming(action === 'approve' || action === 'release');
		setBulkAction(action);
	};

	const closeBulk = () => {
		if (bulkWorking) return;
		setBulkAction(null);
		setBulkConfirming(false);
	};

	const eligibleCount =
		bulkAction === 'approve'
			? selectedRows.filter(isApprovable).length
			: bulkAction === 'release'
				? selectedRows.filter(isReleasable).length
				: selectedRows.filter(isOpen).length;

	const requestBulkConfirm = () => {
		if (bulkAction === 'cancel' && !bulkReason.trim()) {
			setBulkError('A reason is required.');
			return;
		}
		if (bulkAction === 'set_ship_date' && !bulkShipDate) {
			setBulkError('Choose a date.');
			return;
		}
		setBulkError('');
		setBulkConfirming(true);
	};

	const runBulk = async () => {
		setBulkWorking(true);
		setBulkError('');
		const result = await bulkOrderAction({
			orderIds: Array.from(selectedIds),
			action: bulkAction,
			reason: bulkAction === 'cancel' ? bulkReason.trim() : undefined,
			anticipatedShipDate: bulkAction === 'set_ship_date' ? bulkShipDate : undefined,
		});
		setBulkWorking(false);
		if (!result?.success) {
			setBulkError(result?.error?.message || 'Bulk action failed.');
			return;
		}

		const { summary, results } = result.data;
		const parts = [`${summary.ok} ${ACTION_LABELS[bulkAction].verb}`];
		if (summary.skipped) parts.push(`${summary.skipped} skipped`);
		if (summary.failed) parts.push(`${summary.failed} failed`);
		(summary.failed ? toast.error : summary.skipped ? toast.warn : toast.success)(parts.join(', '));

		const orderNumbers = new Map(orders.map((o) => [o.order_id, o.order_number]));
		const notApplied = results
			.filter((r) => r.status !== 'ok')
			.map((r) => ({ ...r, order_number: orderNumbers.get(r.order_id) || r.order_id }));
		setBulkResults(notApplied.length ? { action: bulkAction, items: notApplied } : null);

		setBulkAction(null);
		setBulkConfirming(false);
		setSelectedIds(new Set());
		setRefreshKey((k) => k + 1);
	};

	// Shown while the column is hovered or anything is selected; focus-visible
	// keeps them reachable by keyboard while hidden.
	const checkboxVisibility = `transition-opacity ${
		selectColumnHovered || selectedIds.size > 0 ? 'opacity-100' : 'opacity-0 focus-visible:opacity-100'
	}`;

	const columns = [
		...(canSelect
			? [
					{
						key: '__select',
						header: (
							<RowCheckbox
								ariaLabel='Select all orders on this page'
								checked={allSelected}
								indeterminate={someSelected}
								onChange={toggleSelectAll}
								className={checkboxVisibility}
							/>
						),
						sortable: false,
						cellProps: {
							onMouseEnter: () => setSelectColumnHovered(true),
							onMouseLeave: () => setSelectColumnHovered(false),
						},
						render: (_value, row) => (
							<RowCheckbox
								ariaLabel={`Select order ${row.order_number}`}
								checked={selectedIds.has(row.order_id)}
								onChange={() => toggleRow(row.order_id)}
								className={checkboxVisibility}
							/>
						),
					},
				]
			: []),
		{
			key: 'order_number',
			header: 'Order Number',
			render: (value, row) => {
				// Snapshotted on the order itself at ingest -- reflects whatever
				// this order's mode was when it was created, even if the store has
				// since been flipped to live (see storeMode.service.ts's isTestModeOrder).
				const isTestModeOrder = (row.mode || 'test') === 'test';
				return (
					<Link
						to={`/orders/${row.order_id}`}
						className='text-black font-medium hover:underline'
						data-tooltip-id={ERP_JOB_TOOLTIP_ID}
						data-tooltip-content={
							row.erp_order_number ? `Pace Job #${row.erp_order_number}` : 'Not sent to Pace yet'
						}
					>
						{value}
						{isTestModeOrder && (
							<span className='ml-2 inline-block rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-xs font-medium'>
								TEST MODE
							</span>
						)}
					</Link>
				);
			},
		},
		// { key: 'store_name', header: 'Store' },
		{
			key: 'order_date',
			header: 'Order Date',
			render: (value) => (value ? formatDate(value) : ''),
		},
		{
			key: 'anticipated_ship_date',
			header: 'Expected Ship',
			render: (value, row) =>
				value ? (
					<span className='inline-flex items-center gap-1'>
						{formatShipDate(value)}
						{row.anticipated_ship_date_source === 'MANUAL' && (
							<Tooltip icon={PencilLine} content='Set by an admin' />
						)}
					</span>
				) : (
					'-'
				),
		},
		{
			key: 'customer_name',
			header: 'Customer',
			render: (value) => value || '-',
		},
		{
			key: 'total_price',
			header: 'Total',
			render: (value, row) =>
				row.payment_type === 'LOYALTY_POINTS'
					? `${Math.round(Number(row.points_required)).toLocaleString()} pts`
					: value,
		},
		{
			key: 'state',
			header: 'Status',
			render: (_value, row) => {
				const { text, bucket } = getListStatus({
					state: row.state,
					erpOrderNumber: row.erp_order_number,
					paceJobStatus: row.pace_job_status,
					customFulfillmentOnly: row.custom_fulfillment_only,
					shipped: row.shipped,
				});
				return <StatusBadge status={bucket} text={text} />;
			},
		},
		...(view === VIEW_APPROVAL
			? [
					{
						key: 'requested_at',
						header: 'Awaiting Since',
						render: (value) => (value ? new Date(value).toLocaleString() : '-'),
					},
					{
						key: '__decision',
						header: '',
						sortable: false,
						render: (_value, row) => (
							<div className='flex gap-2 justify-end'>
								<button
									type='button'
									className='btn-secondary-small'
									onClick={() => {
										setDeclineReason('');
										setDeclineTarget(row);
									}}
								>
									Decline
								</button>
								<button type='button' className='btn-primary-small' onClick={() => setApproveTarget(row)}>
									Approve
								</button>
							</div>
						),
					},
				]
			: []),
		...(view === VIEW_REVIEW
			? [
					{
						key: '__release',
						header: '',
						sortable: false,
						render: (_value, row) => (
							<div className='flex justify-end'>
								<button type='button' className='btn-primary-small' onClick={() => setReleaseTarget(row)}>
									Release
								</button>
							</div>
						),
					},
				]
			: []),
	];

	const extraSegments = [
		...(canApprove ? [{ value: VIEW_APPROVAL, label: 'Needs approval' }] : []),
		...(isAdmin ? [{ value: VIEW_REVIEW, label: 'Needs review' }] : []),
	];
	const segments = extraSegments.length ? [{ value: VIEW_ALL, label: 'All orders' }, ...extraSegments] : [];
	const pills = range.from
		? [{ key: 'orderDate', label: `Ordered: ${range.label || `${formatShipDate(range.from)} – ${formatShipDate(range.to)}`}` }]
		: [];

	const selectedCount = selectedIds.size;
	const plural = (n) => `${n} order${n === 1 ? '' : 's'}`;

	return (
		<div className='py-8 pr-8'>
			<h1 className='text-2xl font-light tracking-tight mb-6'>All Orders</h1>

			<FilterPills
				segments={segments}
				activeSegment={view}
				onSegmentChange={changeView}
				pills={pills}
				onRemove={(key) => key === 'orderDate' && changeRange({})}
				onOpenPicker={() => setPickerOpen((open) => !open)}
				className={pickerOpen ? 'mb-3' : ''}
			>
				<div className='relative'>
					<Search className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400' />
					<input
						className='w-52 form-input pl-9'
						placeholder='Search orders'
						aria-label='Search orders'
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
							setPage(0);
						}}
					/>
				</div>
			</FilterPills>

			{pickerOpen && (
				<div className='mb-5 flex flex-wrap items-end gap-4 rounded-md border border-gray-200 px-4 py-3'>
					<div>
						<label>Order date</label>
						<DateRangePicker value={range} onChange={changeRange} className='w-64' placeholder='All dates' />
					</div>
					{range.from && (
						<button type='button' className='text-sm text-gray-500 hover:text-black' onClick={() => changeRange({})}>
							Clear dates
						</button>
					)}
				</div>
			)}

			{view === VIEW_APPROVAL && activeStore && !activeStore.requireOrderApproval && (
				<p className='mb-5 text-sm text-gray-500'>
					Order approval isn’t turned on for this store, so no orders wait here. Turn on “Require order approval” in
					Settings to route new orders for review before they go to Pace.
				</p>
			)}

			{view === VIEW_REVIEW && activeStore && !activeStore.requireOrderReview && (
				<p className='mb-5 text-sm text-gray-500'>
					Manual review isn’t turned on for this store, so new orders don’t wait here. Any orders still listed were
					held before it was turned off and need to be released. Turn on “Manual review of each order is required” in
					Settings to hold every order for release before it goes to Pace.
				</p>
			)}

			{selectedCount > 0 && (
				<div className='mb-5 flex flex-wrap items-center gap-3 bg-gray-50 border border-gray-200 rounded-md px-4 py-2'>
					<span className='text-sm text-gray-600'>{plural(selectedCount)} selected</span>
					{canApprove && (
						<button type='button' className='btn-secondary-small' onClick={() => openBulk('approve')}>
							Approve
						</button>
					)}
					{isAdmin && (
						<button type='button' className='btn-secondary-small' onClick={() => openBulk('release')}>
							Release
						</button>
					)}
					{isAdmin && (
						<button type='button' className='btn-secondary-small' onClick={() => openBulk('set_ship_date')}>
							Set ship date
						</button>
					)}
					{isAdmin && (
						<button type='button' className='btn-secondary-small' onClick={() => openBulk('cancel')}>
							Cancel orders
						</button>
					)}
					<button
						type='button'
						className='text-sm text-gray-500 hover:text-black'
						onClick={() => setSelectedIds(new Set())}
					>
						Clear selection
					</button>
				</div>
			)}

			{bulkResults && (
				<div className='mb-5 border border-amber-200 bg-amber-50 rounded-md px-4 py-3 text-sm'>
					<div className='flex items-center justify-between'>
						<span className='font-medium text-amber-800'>
							{plural(bulkResults.items.length)} not {ACTION_LABELS[bulkResults.action].verb}
						</span>
						<button
							type='button'
							className='text-gray-500 hover:text-black'
							onClick={() => setBulkResults(null)}
						>
							Dismiss
						</button>
					</div>
					<ul className='mt-2 space-y-1 text-amber-900'>
						{bulkResults.items.map((item) => (
							<li key={item.order_id}>
								<Link to={`/orders/${item.order_id}`} className='font-medium hover:underline'>
									{item.order_number}
								</Link>{' '}
								— {item.status === 'skipped' ? 'skipped' : 'failed'}: {item.message}
							</li>
						))}
					</ul>
				</div>
			)}

			<DataTable
				columns={columns}
				data={orders}
				loading={loading}
				emptyHeading={
					view === VIEW_APPROVAL
						? 'No orders waiting on approval'
						: view === VIEW_REVIEW
							? 'No orders waiting on review'
							: 'No orders found'
				}
				rowClassName={(row) => (highlightOrderId && row.order_id === highlightOrderId ? 'bg-amber-50' : '')}
			/>
			{/* One shared instance for every order-number link in the table. */}
			<ReactTooltip id={ERP_JOB_TOOLTIP_ID} className='max-w-xs z-50' />

			<div className='flex flex-wrap items-center justify-between gap-4 mt-8'>
				<Pagination pageCount={Math.ceil(total / pageSize)} currentPage={page} onPageChange={setPage} />
				<label className='flex items-center gap-2 text-sm text-gray-500 ml-auto'>
					Rows per page
					<select
						className='form-input w-16 py-1'
						value={pageSize}
						onChange={(e) => changePageSize(Number(e.target.value))}
					>
						{PAGE_SIZE_OPTIONS.map((size) => (
							<option key={size} value={size}>
								{size}
							</option>
						))}
					</select>
				</label>
			</div>

			<DialogModal
				open={Boolean(bulkAction) && !bulkConfirming}
				setOpen={(open) => !open && closeBulk()}
				title={bulkAction ? ACTION_LABELS[bulkAction].title : ''}
			>
				<p className='text-sm text-gray-600'>
					{plural(selectedCount)} selected
					{eligibleCount < selectedCount &&
						` — ${eligibleCount} can be ${bulkAction ? ACTION_LABELS[bulkAction].verb : ''}; the rest will be skipped`}
					.
				</p>

				{bulkAction === 'cancel' && (
					<div>
						<label>Reason</label>
						<input
							className='form-input w-full'
							value={bulkReason}
							onChange={(e) => setBulkReason(e.target.value)}
						/>
						<p className='text-xs text-gray-500 mt-1'>
							Orders already in Pace are also marked cancelled there.
						</p>
					</div>
				)}

				{bulkAction === 'set_ship_date' && (
					<div>
						<label>Expected ship date</label>
						<input
							type='date'
							className='form-input w-full'
							min={shipCalendarToday()}
							value={bulkShipDate}
							onChange={(e) => setBulkShipDate(e.target.value)}
						/>
						{isWeekendDate(bulkShipDate) && (
							<p className='text-xs text-amber-700 mt-1'>That date is a weekend.</p>
						)}
						<p className='text-xs text-gray-500 mt-1'>
							Orders already in Pace get their Promise Date and Scheduled Ship Date updated too.
						</p>
					</div>
				)}

				{bulkError && <div className='text-sm text-pi-red'>{bulkError}</div>}

				<div className='flex gap-3 pt-2'>
					<button
						type='button'
						className={bulkAction === 'cancel' ? 'btn-alert' : 'btn-primary'}
						disabled={eligibleCount === 0}
						onClick={requestBulkConfirm}
					>
						Apply
					</button>
					<button type='button' className='btn-secondary' onClick={closeBulk}>
						Close
					</button>
				</div>
			</DialogModal>

			<DialogModal
				open={Boolean(bulkAction) && bulkConfirming}
				setOpen={(open) => !open && closeBulk()}
				title={
					bulkAction === 'approve'
						? `Approve ${plural(eligibleCount)}?`
						: bulkAction === 'release'
							? `Release ${plural(eligibleCount)} to the ERP?`
							: bulkAction === 'cancel'
							? `Cancel ${plural(eligibleCount)}?`
							: `Change the ship date on ${plural(eligibleCount)}?`
				}
			>
				{bulkAction === 'approve' && (
					<p className='text-sm text-gray-600'>
						They move to Ready for ERP and are sent to Pace on the next submission run. This can’t be undone.
					</p>
				)}
				{bulkAction === 'release' && (
					<p className='text-sm text-gray-600'>
						They move to Ready for ERP and are sent to Pace on the next submission run. This can’t be undone.
					</p>
				)}
				{bulkAction === 'cancel' && (
					<p className='text-sm text-gray-600'>
						Reason: “{bulkReason.trim()}”. Orders already in Pace are marked cancelled there too. This can’t be
						undone.
					</p>
				)}
				{bulkAction === 'set_ship_date' && (
					<p className='text-sm text-gray-600'>
						The expected ship date becomes {formatShipDate(bulkShipDate)}. Orders already in Pace get their Promise
						Date and Scheduled Ship Date updated too.
					</p>
				)}
				{eligibleCount < selectedCount && (
					<p className='text-sm text-gray-500'>
						{plural(selectedCount - eligibleCount)} of the {selectedCount} selected will be skipped.
					</p>
				)}

				{bulkError && <div className='text-sm text-pi-red'>{bulkError}</div>}

				<div className='flex justify-end gap-2 pt-2'>
					<button
						type='button'
						className='btn-secondary'
						disabled={bulkWorking}
						onClick={() =>
							bulkAction === 'approve' || bulkAction === 'release' ? closeBulk() : setBulkConfirming(false)
						}
					>
						Back
					</button>
					<button
						type='button'
						className={bulkAction === 'cancel' ? 'btn-alert' : 'btn-primary'}
						disabled={bulkWorking || eligibleCount === 0}
						onClick={runBulk}
					>
						{bulkWorking
							? 'Working…'
							: bulkAction === 'approve'
								? 'Approve'
								: bulkAction === 'release'
									? 'Release'
									: bulkAction === 'cancel'
									? 'Cancel orders'
									: 'Change ship date'}
					</button>
				</div>
			</DialogModal>

			<DialogModal
				open={Boolean(approveTarget)}
				setOpen={(open) => !open && !decisionWorking && setApproveTarget(null)}
				title='Approve this order?'
			>
				<p className='text-sm text-gray-600'>
					Order {approveTarget?.order_number} moves to Ready for ERP and is sent to Pace on the next submission run.
					This cannot be undone.
				</p>
				<div className='flex justify-end gap-2 pt-2'>
					<button type='button' className='btn-secondary' onClick={() => setApproveTarget(null)}>
						Back
					</button>
					<button type='button' className='btn-primary' disabled={decisionWorking} onClick={handleApproveConfirm}>
						{decisionWorking ? 'Approving…' : 'Approve order'}
					</button>
				</div>
			</DialogModal>

			<DialogModal
				open={Boolean(releaseTarget)}
				setOpen={(open) => !open && !decisionWorking && setReleaseTarget(null)}
				title='Release this order to the ERP?'
			>
				<p className='text-sm text-gray-600'>
					Order {releaseTarget?.order_number} moves to Ready for ERP and is sent to Pace on the next submission run.
					This cannot be undone.
				</p>
				<div className='flex justify-end gap-2 pt-2'>
					<button type='button' className='btn-secondary' onClick={() => setReleaseTarget(null)}>
						Back
					</button>
					<button type='button' className='btn-primary' disabled={decisionWorking} onClick={handleReleaseConfirm}>
						{decisionWorking ? 'Releasing…' : 'Release order'}
					</button>
				</div>
			</DialogModal>

			<DialogModal
				open={Boolean(declineTarget)}
				setOpen={(open) => !open && !decisionWorking && setDeclineTarget(null)}
				title='Decline this order?'
			>
				<p className='text-sm text-gray-600'>
					Order {declineTarget?.order_number} will be cancelled. This cannot be undone.
				</p>
				<div className='flex flex-col'>
					<label className='text-xs text-gray-500'>Reason for declining (required)</label>
					<textarea
						className='form-input mt-2'
						rows={3}
						value={declineReason}
						onChange={(e) => setDeclineReason(e.target.value)}
					/>
				</div>
				<div className='flex justify-end gap-2 pt-2'>
					<button type='button' className='btn-secondary' onClick={() => setDeclineTarget(null)}>
						Back
					</button>
					<button
						type='button'
						className='btn-alert'
						disabled={decisionWorking || !declineReason.trim()}
						onClick={handleDeclineConfirm}
					>
						{decisionWorking ? 'Declining…' : 'Decline order'}
					</button>
				</div>
			</DialogModal>
		</div>
	);
}
